import React, { useState, useRef, useEffect } from 'react';
import { Heart, Coffee, Gift, Sparkles, Send, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import PaymentMethodModal from './PaymentMethodModal';
import { getPendingPurchase, clearPendingPurchase } from './PaymentMethodModal';

const QUICK_AMOUNTS = [
  { amount: 0.50, label: '$0.50', icon: Coffee },
  { amount: 2.00, label: '$2', icon: Heart },
  { amount: 5.00, label: '$5', icon: Gift },
];

export const DonationPopup = ({ creatorId, creatorName, filmId, trigger }) => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [showCustom, setShowCustom] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const popupRef = useRef(null);
  const triggerRef = useRef(null);

  // Auto-open payment modal if redirected back from add-card flow
  useEffect(() => {
    const pending = getPendingPurchase();
    if (pending?.autoOpen && pending.paymentType === 'donation' && pending.creatorId === creatorId) {
      clearPendingPurchase();
      setSelectedAmount(pending.amount);
      setTimeout(() => setShowPaymentModal(true), 500);
    }
  }, [creatorId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target) &&
          triggerRef.current && !triggerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleDonate = (e) => {
    e.stopPropagation();
    if (!isAuthenticated) { navigate('/login'); return; }

    const amount = showCustom ? parseFloat(customAmount) : selectedAmount;
    if (!amount || amount <= 0) { toast.error('Select an amount'); return; }
    if (amount < 0.50) { toast.error('Minimum donation is $0.50'); return; }

    setIsOpen(false);
    setShowPaymentModal(true);
  };

  const donationAmount = showCustom ? parseFloat(customAmount) || 0 : selectedAmount || 0;

  const handleTriggerClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsOpen(!isOpen);
  };

  return (
    <>
      <div className="relative inline-flex items-center">
        {trigger ? (
          <div ref={triggerRef} onClick={handleTriggerClick}>{trigger}</div>
        ) : (
          <button ref={triggerRef} onClick={handleTriggerClick}
            className="ml-2 p-1.5 rounded-full bg-[#8B5CF6]/20 hover:bg-[#8B5CF6]/30 transition-colors group"
            data-testid="donation-popup-trigger" title={`Support ${creatorName}`}>
            <Sparkles className="w-4 h-4 text-[#8B5CF6] group-hover:text-[#A78BFA]" />
          </button>
        )}

        {isOpen && (
          <div ref={popupRef}
            className="absolute z-50 top-full left-0 mt-2 w-64 p-4 rounded-xl bg-[#0F0B1E] border border-[#8B5CF6]/30 shadow-2xl"
            onClick={(e) => e.stopPropagation()} data-testid="donation-popup">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#8B5CF6]" />
                <span className="text-sm font-medium">Support {creatorName}</span>
              </div>
              <button onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}
                className="p-1 rounded-full hover:bg-white/10">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            <div className="grid grid-cols-4 gap-2 mb-3">
              {QUICK_AMOUNTS.map(({ amount, label, icon: Icon }) => (
                <button key={amount}
                  onClick={(e) => { e.stopPropagation(); setSelectedAmount(amount); setShowCustom(false); }}
                  className={`p-2 rounded-lg border text-center transition-all ${
                    selectedAmount === amount && !showCustom
                      ? 'border-[#8B5CF6] bg-[#8B5CF6]/20 text-white'
                      : 'border-white/10 text-gray-400 hover:border-white/20'
                  }`} data-testid={`popup-donate-${amount}`}>
                  <Icon className="w-3 h-3 mx-auto mb-0.5" />
                  <span className="text-xs font-bold block">{label}</span>
                </button>
              ))}
              <button
                onClick={(e) => { e.stopPropagation(); setShowCustom(true); setSelectedAmount(null); }}
                className={`p-2 rounded-lg border text-center transition-all ${
                  showCustom ? 'border-[#00FF9D] bg-[#00FF9D]/20 text-white' : 'border-white/10 text-gray-400 hover:border-white/20'
                }`} data-testid="popup-donate-custom">
                <span className="text-sm font-bold block">$?</span>
                <span className="text-[9px] text-gray-500">Custom</span>
              </button>
            </div>

            {showCustom && (
              <div className="mb-3">
                <div className="relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-bold">$</span>
                  <input type="number" value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    onClick={(e) => e.stopPropagation()} placeholder="Amount" min="0.50" step="0.50"
                    className="w-full pl-6 pr-3 py-2 text-sm bg-[#030014] border border-[#8B5CF6]/20 rounded-lg focus:border-[#8B5CF6] outline-none"
                    data-testid="popup-custom-input" />
                </div>
              </div>
            )}

            <button onClick={handleDonate}
              disabled={!selectedAmount && !customAmount}
              className="w-full py-2 rounded-lg font-semibold text-xs transition-all disabled:opacity-50 disabled:cursor-not-allowed bg-[#8B5CF6] hover:bg-[#7C3AED] text-white flex items-center justify-center gap-2"
              data-testid="popup-send-btn">
              <Send className="w-3 h-3" />
              {showCustom && customAmount
                ? `Donate $${parseFloat(customAmount).toFixed(2)}`
                : selectedAmount
                  ? `Donate $${selectedAmount.toFixed(2)}`
                  : 'Select amount'}
            </button>

            <p className="text-[9px] text-gray-600 text-center mt-2">
              100% to creator (minus fees)
            </p>
          </div>
        )}
      </div>

      {/* Payment Method Modal */}
      <PaymentMethodModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        paymentType="donation"
        amount={donationAmount}
        creatorId={creatorId}
        filmId={filmId}
        creatorName={creatorName}
        onSuccess={() => {
          setSelectedAmount(null);
          setCustomAmount('');
          setShowCustom(false);
        }}
      />
    </>
  );
};
